/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { isEqual } from 'lodash'
import { useEffect, useMemo, useReducer, useRef } from 'react'
import type { ContactJson } from 'app/types/entities'
import type { MessageJson } from 'app/types/entities'
import { ARCHIVE_MESSAGE_MUTATION, MARK_MESSAGE_READ_MUTATION } from 'app/graphql/messageMutations'
import { INBOX_THREADS_QUERY } from 'app/components/inbox/graphql'
import { useMessagePoll } from 'app/hooks/useMessagePoll'
import { useSidebarCounts } from 'app/hooks/useSidebarCounts'
import { notify, notifyError, notifySuccess } from 'app/services/notify.client'
import { gqlMutation } from 'app/lib/graphqlClient'
import { gqlQuery } from 'app/lib/graphqlClient'
import { useLocationOptOuts } from 'app/hooks/useLocationOptOuts'
import { useInboxSearch, type InboxSearchResolution } from 'app/hooks/useInboxSearch'
import { fetchUnreadOptOutSuppression } from 'app/lib/unreadOptOuts'
import { buildSearchTargetClause } from 'app/lib/inboxSearch'
import { excludeHeld } from 'app/lib/heldMessages'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import {
  aggregateConversations,
  aggregateUnreads,
  buildConversationListSearch,
  buildOptedOutSearches,
  createInitialInboxState,
  fetchMessagesPage,
  getActiveConversation,
  getConversationPhone,
  matchesSelectedConversation,
  mergeMessagePages,
  partitionByOptOut,
  resolveInboxSelection,
  sortMessagesChronologically,
} from 'app/lib/inboxConversations'
import { inboxReducer } from 'app/hooks/inboxReducer'
import type { ConversationType, InboxLoaderData } from 'app/lib/inboxTypes'

export type { ConversationType, InboxAction, InboxLoaderData, InboxState } from 'app/lib/inboxTypes'
export {
  aggregateConversations,
  aggregateUnreads,
  buildConversationListSearch,
  buildOptedOutSearches,
  getConversationPhone,
  partitionByOptOut,
  resolveInboxSelection,
} from 'app/lib/inboxConversations'
export { inboxReducer } from 'app/hooks/inboxReducer'

// Which of the given threads currently have the SMS agent engaged (aiEnabled === true). Used to
// skip auto-mark-read on those threads so a viewer does not suppress the agent's reply (TRK-5909).
async function fetchAiControlledThreadIds(threadIds: string[]): Promise<Set<string>> {
  if (threadIds.length === 0) return new Set()
  // network-only: a cached answer from before the AI was turned on says "off", and marking read
  // then fails on every pass (TRK-5955).
  const { data, error } = await gqlQuery(
    INBOX_THREADS_QUERY,
    { search: `id:(${threadIds.join(' OR ')})`, pagination: { limit: threadIds.length } },
    { requestPolicy: `network-only` },
  )
  if (error) {
    // On a failed lookup, fall back to marking read (prior behavior) rather than stranding unread.
    logError(`Error checking AI-controlled threads for auto-mark-read`, error.message)
    return new Set()
  }
  const ids = new Set<string>()
  for (const thread of data?.threads?.hits ?? []) {
    if (thread.aiEnabled === true) ids.add(thread.id)
  }
  return ids
}

export function useInboxController({ data, searchParams }: { data: InboxLoaderData; searchParams: URLSearchParams }) {
  const [state, dispatch] = useReducer(inboxReducer, data.threadId, createInitialInboxState)
  const { adjustInboxCount } = useSidebarCounts()

  const isLoading = state.isLoading
  const hasSynced = state.hasSynced
  const isUpdatingMessages = state.pendingMutationCount > 0
  const threadId = state.selectedThreadId
  const contactId = state.selectedContactId
  const selectedContact = state.selectedContact
  const conversations = state.conversations
  const messages = state.messages
  const unreadLocations = state.unreadLocations
  const optimisticMessages = state.optimisticMessages

  // Resolves the query against contacts first; the messages index cannot match names.
  const search = useInboxSearch(data.query, data.locationId)
  const searchClause = useMemo(
    () => (search.status === `resolved` ? buildSearchTargetClause(search.targets) : undefined),
    [search],
  )
  // Key on the string, not the resolution object: an unchanged resolution as a fresh object must not re-fire.
  const searchKey = `${search.status}:${searchClause ?? ``}`
  const optOuts = useLocationOptOuts(data.locationId)
  // Apply opt-out suppression once so every consumer (list, auto-select, activeConversation) agrees.
  const visibleConversations = useMemo(
    () => partitionByOptOut(conversations, optOuts, data.filter, threadId),
    [conversations, optOuts, data.filter, threadId],
  )

  const isUpdatingMessagesRef = useRef(isUpdatingMessages)
  isUpdatingMessagesRef.current = isUpdatingMessages
  // Mirror into a ref: poll-driven syncInbox would read a stale value from the render closure.
  const optOutsRef = useRef<ReadonlyMap<string, string>>(optOuts)
  optOutsRef.current = optOuts
  const searchRef = useRef<{ status: InboxSearchResolution[`status`]; clause?: string }>({
    status: search.status,
    clause: searchClause,
  })
  searchRef.current = { status: search.status, clause: searchClause }
  const conversationsRef = useRef<ConversationType[]>(conversations)
  conversationsRef.current = conversations
  const threadIdRef = useRef<string>(data.threadId)
  const contactIdRef = useRef<string | undefined>(undefined)
  const latestSyncRequestIdRef = useRef(0)
  const displayedMessagesRef = useRef<MessageJson[]>([])
  // Threads the user marked unread, so auto-mark-read skips them. Scoped to the visit.
  const autoMarkSkipRef = useRef<Set<string>>(new Set())
  // Last locationId the change effect reacted to, to tell an initial settle from a real switch.
  const lastLocationIdRef = useRef<string | undefined>(undefined)
  threadIdRef.current = threadId
  contactIdRef.current = contactId

  const setIsLoading = (next: boolean) => dispatch({ type: `SET_LOADING`, isLoading: next })
  const setIsUpdatingMessages = (next: boolean) => dispatch({ type: next ? `BEGIN_MUTATION` : `END_MUTATION` })
  const setConversations = (next: ConversationType[] | ((prev: ConversationType[]) => ConversationType[])) =>
    dispatch({ type: `UPDATE_CONVERSATIONS`, updater: typeof next === `function` ? next : () => next })
  const setOptimisticMessages = (
    next: MessageJson[] | undefined | ((prev?: MessageJson[]) => MessageJson[] | undefined),
  ) => dispatch({ type: `UPDATE_OPTIMISTIC_MESSAGES`, updater: typeof next === `function` ? next : () => next })

  const syncInbox = async (params?: { nextThreadId?: string; nextContactId?: string }) => {
    if (!data.locationId) {
      dispatch({ type: `SYNC_CLEARED` })
      return
    }

    const activeThreadId = params?.nextThreadId ?? threadIdRef.current
    const activeContactId = params?.nextContactId ?? contactIdRef.current
    const requestId = latestSyncRequestIdRef.current + 1
    latestSyncRequestIdRef.current = requestId

    try {
      // While resolving, don't fall back to an unfiltered fetch (the whole list would flash back).
      if (searchRef.current.status === `resolving`) return
      const activeSearchClause = searchRef.current.clause
      // A resolved query with no clause matches nothing; skip the fetch to show "no results".
      const hasNoSearchMatches = searchRef.current.status === `resolved` && !activeSearchClause
      const conversationSearch = buildConversationListSearch(data.locationId, data.filter, activeSearchClause)
      const unreadSearch = excludeHeld(`NOT read:true AND NOT archived:true`)
      // Widen the fetch to every member threadId of the active group (TRK-5414 phone-pair split).
      const activeMemberThreadIds = activeThreadId
        ? (conversationsRef.current.find((conversation) => conversation.threadId === activeThreadId)
            ?.memberThreadIds ?? [activeThreadId])
        : []
      const threadSearchParts: string[] = []
      if (activeMemberThreadIds.length > 1) {
        threadSearchParts.push(`(${activeMemberThreadIds.map((id) => `threadId:"${id}"`).join(` OR `)})`)
      } else if (activeThreadId) threadSearchParts.push(`threadId:"${activeThreadId}"`)
      else if (activeContactId) threadSearchParts.push(`contactId:"${activeContactId}"`)
      const threadSearch = threadSearchParts.join(` AND `)

      // Opted out uses phone-targeted queries. The opt-out map resolves on its own query, so the
      // first sync can fetch nothing; the next poll self-corrects. Do not block here to "fix" it.
      const isOptedOutFilter = data.filter === `opted_out`
      const optedOutSearches = isOptedOutFilter
        ? buildOptedOutSearches(data.locationId, [...optOutsRef.current.keys()], activeSearchClause)
        : []

      const [conversationMessages, unreadMessages, threadMessages] = await Promise.all([
        hasNoSearchMatches
          ? Promise.resolve([] as MessageJson[])
          : isOptedOutFilter
            ? Promise.all(optedOutSearches.map((search) => fetchMessagesPage(search, `DESC`, 500))).then(
                mergeMessagePages,
              )
            : fetchMessagesPage(conversationSearch, `DESC`, 500, data.filter === `archived`),
        fetchMessagesPage(unreadSearch, `DESC`, 1000),
        // includeArchived returns BOTH states, so this is safe for live threads and required for archived ones.
        threadSearch ? fetchMessagesPage(threadSearch, `ASC`, 500, true) : Promise.resolve([] as MessageJson[]),
      ])

      if (requestId !== latestSyncRequestIdRef.current) return

      // optOutsRef covers only the open location; these counts span every location the picker lists.
      const unreadSuppression = await fetchUnreadOptOutSuppression(
        unreadMessages.filter((message) => !message.sentByUserId && !message.isAgent),
      )
      if (requestId !== latestSyncRequestIdRef.current) return

      const { byLocation, byThread } = aggregateUnreads(unreadMessages, data.locationId, unreadSuppression)
      // Recompute the active thread's unread from threadMessages (accurate despite index lag), per member threadId.
      if (activeThreadId) {
        for (const memberThreadId of activeMemberThreadIds) {
          byThread[memberThreadId] = threadMessages.filter(
            (m) => m.threadId === memberThreadId && !m.sentByUserId && !m.isAgent && m.read !== true,
          ).length
        }
      }
      const conversations = aggregateConversations(conversationMessages, byThread)

      dispatch({
        type: `SYNC_APPLIED`,
        conversations,
        unreadLocations: byLocation,
        messages: sortMessagesChronologically(threadMessages) ?? [],
      })
    } catch (err) {
      logError(`Error syncing inbox`, err)
      dispatch({ type: `SYNC_FAILED` })
    }
  }

  const addConversation = (contact: ContactJson, nextThreadId?: string) => {
    dispatch({ type: `ADD_CONVERSATION`, contact, threadId: nextThreadId })
  }

  const selectConversation = (nextThreadId: string, contact?: ContactJson, nextContactId?: string) => {
    dispatch({ type: `SELECT_CONVERSATION`, threadId: nextThreadId, contactId: nextContactId, contact })
  }

  const resetInboxState = () => {
    dispatch({ type: `RESET_INBOX` })
  }

  // Clear the active selection but keep the synced list (mobile "back" without a resync flash).
  const clearSelection = () => {
    dispatch({ type: `CLEAR_RESOLVED_SELECTION` })
  }

  // Row-level "Mark as unread" for any conversation; the fetch runs behind the badge and rolls back on refusal.
  const markConversationUnread = (message: MessageJson) => {
    const unreadThreadId = message.threadId
    if (!unreadThreadId) return
    // The open thread already has its messages, so it needs no fetch.
    if (unreadThreadId === threadIdRef.current) {
      bg(markThreadUnread())
      return
    }

    const previousUnread = conversationsRef.current.find((c) => c.threadId === unreadThreadId)?.unread ?? 0
    dispatch({
      type: `SET_THREAD_UNREAD_OPTIMISTIC`,
      threadId: unreadThreadId,
      unread: previousUnread || 1,
      locationId: message.locationId,
    })
    if (previousUnread === 0) adjustInboxCount(1)

    bg(async () => {
      let marked = false
      try {
        const threadMessages = await fetchMessagesPage(`threadId:"${unreadThreadId}"`, `ASC`, 500)
        const latestIncoming = [...threadMessages].reverse().find((m) => m.id && !m.sentByUserId && !m.isAgent)
        if (!latestIncoming) {
          notify(`Nothing to mark unread. No customer messages in this thread`)
        } else {
          const result = await gqlMutation(MARK_MESSAGE_READ_MUTATION, {
            id: latestIncoming.id!,
            read: false,
          })
          if (result.error) {
            logError(`Error marking conversation unread`, result.error.message)
            notifyError(`Error marking conversation unread`)
          } else {
            marked = true
          }
        }
      } catch (err) {
        logError(`Error marking conversation unread`, err)
        notifyError(`Error marking conversation unread`)
      }

      if (!marked) {
        dispatch({
          type: `SET_THREAD_UNREAD_OPTIMISTIC`,
          threadId: unreadThreadId,
          unread: previousUnread,
          locationId: message.locationId,
        })
        if (previousUnread === 0) adjustInboxCount(-1)
        return
      }

      setIsUpdatingMessages(true)
      await syncInbox()
      setIsUpdatingMessages(false)
    })
  }

  const archiveConversation = (message: MessageJson, archived: boolean) => {
    bg(async () => {
      setIsUpdatingMessages(true)
      let filed = false
      try {
        // includeArchived: unarchiving must see the archived messages it flips back.
        const threadMessages = await fetchMessagesPage(`threadId:"${message.threadId!}"`, `ASC`, 500, true)
        const results = await Promise.all(
          threadMessages.map((m) => gqlMutation(ARCHIVE_MESSAGE_MUTATION, { id: m.id!, archived })),
        )
        const firstError = results.find((r) => r.error)
        if (firstError?.error) {
          logError(`Error archiving conversation`, firstError.error.message)
          notifyError(`Error archiving conversation`)
        } else {
          filed = true
          notifySuccess(`Conversation ${archived ? `archived` : `unarchived`} successfully`)
        }
      } catch (err) {
        logError(`Error archiving conversation`, err)
        notifyError(`Error archiving conversation`)
      }
      // Drop the row only once the server agrees; a single rejected message keeps it in the list.
      if (filed) dispatch({ type: `ARCHIVE_LOCAL_REMOVE`, threadId: message.threadId! })
      await syncInbox()
      setIsUpdatingMessages(false)
    })
  }

  const displayedMessages = useMemo(
    () => sortMessagesChronologically(optimisticMessages ?? messages),
    [optimisticMessages, messages],
  )
  displayedMessagesRef.current = displayedMessages ?? []

  const activeConversation = useMemo(
    () => visibleConversations.find((conversation) => matchesSelectedConversation(conversation, threadId, contactId)),
    [visibleConversations, threadId, contactId],
  )

  const selectedPhone = useMemo(() => getConversationPhone(activeConversation), [activeConversation])

  // Every member thread of the open conversation (TRK-5414 group can span two threadIds).
  const activeThreadIds = useMemo(
    () => new Set([threadId, ...(activeConversation?.memberThreadIds ?? [])].filter(Boolean)),
    [threadId, activeConversation],
  )

  // Auto-mark targets keyed by id, not array identity: displayedMessages is rebuilt every poll.
  const autoMarkTargetIds = useMemo(
    () =>
      (displayedMessages ?? [])
        .filter(
          (m) => m.threadId && activeThreadIds.has(m.threadId) && m.id && !m.sentByUserId && !m.isAgent && !m.read,
        )
        .map((m) => m.id!),
    [displayedMessages, activeThreadIds],
  )
  const autoMarkTargetKey = autoMarkTargetIds.join(`,`)

  /**
   * `auto` is the open-thread auto-mark. Its refusal (the AI took the thread since the check)
   * shows no toast and stops auto-marking the thread until it is opened again; otherwise the
   * rollback makes the message unread and the auto-mark retries on a loop (TRK-5955).
   */
  const markMessagesRead = async (messageIds: string[], targetRead: boolean, locationId?: string, auto = false) => {
    if (messageIds.length === 0) return
    setIsUpdatingMessages(true)
    const activeConversation = getActiveConversation(state)
    const previousUnread = activeConversation?.unread ?? 0
    const nextUnread = targetRead ? Math.max(0, previousUnread - messageIds.length) : previousUnread + messageIds.length

    // Optimistic update first, then the mutation; roll back on rejection.
    dispatch({
      type: `MARK_READ_OPTIMISTIC`,
      updatedIds: messageIds,
      read: targetRead,
      unread: nextUnread,
      locationId,
    })

    // Adjust the sidebar badge locally when the thread crosses the zero-unread boundary (index lag).
    const wasUnreadThread = previousUnread > 0
    const willBeUnreadThread = nextUnread > 0
    const inboxDelta = wasUnreadThread === willBeUnreadThread ? 0 : willBeUnreadThread ? 1 : -1
    if (inboxDelta !== 0) adjustInboxCount(inboxDelta)

    const results = await Promise.all(
      messageIds.map((id) =>
        gqlMutation(
          MARK_MESSAGE_READ_MUTATION,
          { id, read: targetRead },
          auto ? { suppressErrorToast: true } : undefined,
        ),
      ),
    )
    const firstError = results.find((r) => r.error)
    if (firstError?.error) {
      logError(`Error updating message read state`, firstError.error.message)
      if (auto && threadIdRef.current) autoMarkSkipRef.current.add(threadIdRef.current)
      dispatch({
        type: `MARK_READ_OPTIMISTIC`,
        updatedIds: messageIds,
        read: !targetRead,
        unread: previousUnread,
        locationId,
      })
      if (inboxDelta !== 0) adjustInboxCount(-inboxDelta)
      setIsUpdatingMessages(false)
      return
    }

    await syncInbox()
    setIsUpdatingMessages(false)
  }

  const markThreadRead = async () => {
    const unread = displayedMessagesRef.current.filter((m) => m.id && !m.sentByUserId && !m.isAgent && !m.read)
    if (unread.length === 0) return
    if (threadIdRef.current) autoMarkSkipRef.current.delete(threadIdRef.current)
    await markMessagesRead(
      unread.map((m) => m.id!),
      true,
      unread[0].locationId,
    )
  }

  const markThreadUnread = async () => {
    const latestIncoming = [...displayedMessagesRef.current]
      .reverse()
      .find((m) => m.id && !m.sentByUserId && !m.isAgent)
    if (!latestIncoming) return
    if (latestIncoming.threadId) autoMarkSkipRef.current.add(latestIncoming.threadId)
    await markMessagesRead([latestIncoming.id!], false, latestIncoming.locationId)
  }

  const handleMessageSent = async (message: MessageJson) => {
    setIsUpdatingMessages(true)
    // Empty displayedMessages is a fresh thread; the just-sent message should still appear.
    const currentMessages = displayedMessages ? [...displayedMessages] : []
    // An email's thread holds only mail; the open conversation stays on its text thread.
    const currentThreadId = message.channel === `email` ? threadId || message.threadId : message.threadId || threadId
    const currentLocationId = message.locationId || currentMessages[0]?.locationId
    // Sending retires sticky-unread so the thread does not bounce back to unread.
    if (currentThreadId) autoMarkSkipRef.current.delete(currentThreadId)

    const nextMessages = sortMessagesChronologically(
      currentMessages
        .map((item) =>
          item.createdDate! <= message.createdDate! && item.sentByUserId == null && !item.isAgent
            ? { ...item, read: true }
            : item,
        )
        .concat(currentMessages.some((item) => item.id === message.id) ? [] : [{ ...message, read: true }]),
    )

    if (isEqual(nextMessages, currentMessages)) {
      setIsUpdatingMessages(false)
      return
    }

    const newlyReadCount = currentMessages.filter(
      (item) => item.createdDate! <= message.createdDate! && item.sentByUserId == null && !item.isAgent && !item.read,
    ).length

    dispatch({
      type: `SEND_MESSAGE_OPTIMISTIC`,
      message,
      currentThreadId: currentThreadId ?? undefined,
      newlyReadCount,
      locationId: currentLocationId,
    })

    try {
      const scopeParts: string[] = [`NOT read:true`]
      if (contactId) scopeParts.push(`contactId:"${contactId}"`)
      else if (currentThreadId) scopeParts.push(`threadId:"${currentThreadId}"`)
      const lastReadIso = message.createdDate!
      const unreadInScope = await fetchMessagesPage(scopeParts.join(` AND `), `ASC`, 500)
      const idsToMark = unreadInScope
        .filter((m) => !m.sentByUserId && !m.isAgent && m.createdDate! <= lastReadIso)
        .map((m) => m.id!)
      const results = await Promise.all(
        idsToMark.map((id) => gqlMutation(MARK_MESSAGE_READ_MUTATION, { id, read: true })),
      )
      const firstError = results.find((r) => r.error)
      if (firstError?.error) {
        logError(`Error marking all messages as read`, firstError.error.message)
      }
    } catch (err) {
      logError(`Error marking all messages as read`, err)
    }

    await syncInbox({ nextThreadId: currentThreadId ?? undefined, nextContactId: contactId })
    setIsUpdatingMessages(false)
  }

  useEffect(() => {
    if (!isUpdatingMessages && isLoading) {
      setIsLoading(false)
    }
  }, [isUpdatingMessages, isLoading])

  useEffect(() => {
    if (optimisticMessages == null || messages == null) return
    if (!contactId && messages.length > 0 && messages[0].threadId !== threadId) return
    if (isEqual(messages, optimisticMessages)) {
      setOptimisticMessages(undefined)
    }
  }, [messages, optimisticMessages, threadId, contactId])

  useEffect(() => {
    bg(() => syncInbox())
    // searchKey, not data.query: wait for the query to resolve into phone numbers, then re-run.
  }, [data.locationId, data.filter, searchKey, threadId, contactId])

  useEffect(() => {
    const resolvedSelection = resolveInboxSelection({
      conversations: visibleConversations,
      threadId,
      contactId,
      tabThreadId: searchParams.get(`tab`),
      hasMessages: Boolean(messages?.length),
      hasSelectedContact: Boolean(selectedContact),
      hasOptimisticMessages: Boolean(optimisticMessages),
      selectedContact,
    })

    if (resolvedSelection.type === `select`) {
      if (
        threadId !== resolvedSelection.threadId ||
        contactId !== resolvedSelection.contactId ||
        !isEqual(selectedContact, resolvedSelection.selectedContact)
      ) {
        dispatch({
          type: `SET_RESOLVED_SELECTION`,
          threadId: resolvedSelection.threadId,
          contactId: resolvedSelection.contactId,
          selectedContact: resolvedSelection.selectedContact,
        })
      }
    } else if (resolvedSelection.type === `clear`) {
      if (threadId || contactId || selectedContact || optimisticMessages) {
        dispatch({ type: `CLEAR_RESOLVED_SELECTION` })
      }
    }
  }, [visibleConversations, searchParams, threadId, contactId, selectedContact, optimisticMessages, messages])

  // Reads unfiltered state on purpose: it clears the source list, not the visible one.
  useEffect(() => {
    if (!searchParams.has(`locationId`) && conversations.length > 0) {
      setConversations([])
    }
  }, [searchParams, conversations.length])

  useEffect(() => {
    // A real switch is one non-empty locationId changing to another; the initial settle is not.
    const previousLocationId = lastLocationIdRef.current
    const isRealSwitch =
      Boolean(previousLocationId) && Boolean(data.locationId) && previousLocationId !== data.locationId
    lastLocationIdRef.current = data.locationId
    if (isRealSwitch) {
      dispatch({ type: `LOCATION_CHANGED` })
    } else {
      dispatch({ type: `CLEAR_RESOLVED_SELECTION` })
    }
  }, [data.locationId])

  // Shared message clock so card, badge and drawer fetch on the same edge.
  // One poll sync in flight at a time; mutation-driven calls stay ungated (latest-wins protects them).
  const pollSyncInFlightRef = useRef(false)
  useMessagePoll(() => {
    if (!data.locationId || isUpdatingMessagesRef.current || pollSyncInFlightRef.current) return
    pollSyncInFlightRef.current = true
    bg(
      syncInbox({
        nextThreadId: threadIdRef.current || undefined,
        nextContactId: contactIdRef.current,
      }).finally(() => {
        pollSyncInFlightRef.current = false
      }),
    )
  })

  // Leaving a thread retires its sticky-unread flag. Above the auto-mark effect so cleanup clears the left thread.
  useEffect(() => {
    const visitedThreadId = threadId
    if (!visitedThreadId) return
    return () => {
      autoMarkSkipRef.current.delete(visitedThreadId)
    }
  }, [threadId])

  // Opening a thread marks it read; re-runs on autoMarkTargetKey for messages arriving while open. Skips autoMarkSkipRef.
  useEffect(() => {
    if (!threadId || autoMarkTargetIds.length === 0) return
    if (autoMarkSkipRef.current.has(threadId)) return
    const unread = displayedMessagesRef.current.filter(
      (m) => m.threadId && activeThreadIds.has(m.threadId) && m.id && !m.sentByUserId && !m.isAgent && !m.read,
    )
    if (unread.length === 0) return
    bg(
      (async () => {
        // Don't auto-mark customer messages read while the SMS agent controls the thread: the
        // agent's burst guard treats `read` as its "already replied" marker, so a viewer opening
        // the thread first would suppress the agent's reply (TRK-5909). The API refuses the write
        // too; skipping here avoids the pointless mutation and its optimistic flicker/rollback.
        const aiThreadIds = await fetchAiControlledThreadIds([
          ...new Set(unread.map((m) => m.threadId!).filter(Boolean)),
        ])
        const markable = unread.filter((m) => !aiThreadIds.has(m.threadId!))
        if (markable.length === 0) return
        await markMessagesRead(
          markable.map((m) => m.id!),
          true,
          markable[0].locationId,
          true,
        )
      })(),
    )
  }, [threadId, autoMarkTargetKey])

  return {
    isLoading,
    hasSynced,
    isUpdatingMessages,
    selectedContact,
    threadId,
    contactId,
    conversations: visibleConversations,
    location: data.location,
    unreadLocations,
    displayedMessages,
    activeConversation,
    selectedPhone,
    addConversation,
    selectConversation,
    resetInboxState,
    clearSelection,
    archiveConversation,
    markConversationUnread,
    markThreadRead,
    markThreadUnread,
    handleMessageSent,
    setConversations,
  }
}
